import React, { useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { tokens } from '../design/tokens';
import { useApi, axios, API_BASE_URL } from '../hooks/useApi';

/**
 * Interfaz para los datos devueltos por la API
 */
interface AppDownloadData {
  video_embed_url: string;
  download_button_text: string;
  download_url: string;
  instructions: string[];
}

/**
 * Componente de Carga
 */
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: ${tokens.spacing.xxxl};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${tokens.colors.surfaceMuted};
  border-top: 4px solid ${tokens.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: ${tokens.spacing.lg};

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: ${tokens.colors.text};
  font-size: ${tokens.typography.body.size};
  margin: 0;
`;

/**
 * Componente de Error
 */
const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: ${tokens.spacing.xxxl};
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  color: ${tokens.colors.error};
  margin-bottom: ${tokens.spacing.lg};
`;

const ErrorText = styled.p`
  color: ${tokens.colors.error};
  font-size: ${tokens.typography.body.size};
  font-weight: ${tokens.typography.body.weight};
  margin: 0 0 ${tokens.spacing.lg} 0;
`;

const RetryButton = styled.button`
  background: ${tokens.colors.primary};
  color: ${tokens.colors.textOnDark};
  border: none;
  padding: ${tokens.spacing.md} ${tokens.spacing.xl};
  border-radius: ${tokens.radius.md};
  font-size: ${tokens.typography.body.size};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${tokens.colors.secondary};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

/**
 * Contenedor principal de la página
 */
const PageContainer = styled.div`
  max-width: 1024px;
  margin: 0 auto;
  padding: ${tokens.spacing.xl};
  background: ${tokens.colors.surface};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.md};

  @media (max-width: ${tokens.breakpoints.md}) {
    padding: ${tokens.spacing.lg};
    margin: ${tokens.spacing.md};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing.md};
    margin: ${tokens.spacing.sm};
    border-radius: ${tokens.radius.md};
  }
`;

/**
 * Título de la página
 */
const PageTitle = styled.h1`
  font-size: ${tokens.typography.h1.size};
  line-height: ${tokens.typography.h1.line};
  font-weight: ${tokens.typography.h1.weight};
  color: ${tokens.colors.text};
  text-align: center;
  margin: 0 0 ${tokens.spacing.xxxl} 0;

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: 28px;
    line-height: 36px;
    margin-bottom: ${tokens.spacing.xl};
  }
`;

/**
 * Contenedor del reproductor de video
 */
const VideoContainer = styled.div`
  margin-bottom: ${tokens.spacing.xxxl};
  
  @media (max-width: ${tokens.breakpoints.sm}) {
    margin-bottom: ${tokens.spacing.xl};
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  padding-bottom: 56.25%; /* Proporción 16:9 */
  height: 0;
  overflow: hidden;
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.lg};
  background: ${tokens.colors.surfaceMuted};

  @media (max-width: ${tokens.breakpoints.sm}) {
    border-radius: ${tokens.radius.md};
  }
`;

const VideoIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  border-radius: ${tokens.radius.lg};

  @media (max-width: ${tokens.breakpoints.sm}) {
    border-radius: ${tokens.radius.md};
  }
`;

/**
 * Contenedor del botón de descarga
 */
const DownloadButtonContainer = styled.div`
  text-align: center;
  margin-bottom: ${tokens.spacing.xxxl};

  @media (max-width: ${tokens.breakpoints.sm}) {
    margin-bottom: ${tokens.spacing.xl};
  }
`;

const DownloadButton = styled.a`
  display: inline-block;
  background: ${tokens.colors.primary};
  color: ${tokens.colors.textOnDark};
  text-decoration: none;
  padding: ${tokens.spacing.lg} ${tokens.spacing.xxxl};
  border-radius: ${tokens.radius.md};
  font-size: ${tokens.typography.h3.size};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${tokens.shadow.md};
  border: none;

  &:hover {
    background: ${tokens.colors.secondary};
    transform: translateY(-2px);
    box-shadow: ${tokens.shadow.lg};
  }

  &:active {
    transform: translateY(0);
    box-shadow: ${tokens.shadow.sm};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    width: 100%;
    padding: ${tokens.spacing.md} ${tokens.spacing.xl};
    font-size: ${tokens.typography.body.size};
  }
`;

/**
 * Contenedor de las instrucciones
 */
const InstructionsContainer = styled.div`
  margin-top: ${tokens.spacing.xxxl};

  @media (max-width: ${tokens.breakpoints.sm}) {
    margin-top: ${tokens.spacing.xl};
  }
`;

const InstructionsTitle = styled.h2`
  font-size: ${tokens.typography.h2.size};
  line-height: ${tokens.typography.h2.line};
  font-weight: ${tokens.typography.h2.weight};
  color: ${tokens.colors.text};
  text-align: center;
  margin: 0 0 ${tokens.spacing.xl} 0;

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: 24px;
    line-height: 32px;
    margin-bottom: ${tokens.spacing.lg};
  }
`;

const InstructionsList = styled.ol`
  list-style: none;
  counter-reset: instruction-counter;
  padding: 0;
  margin: 0;
  display: grid;
  gap: ${tokens.spacing.lg};

  @media (max-width: ${tokens.breakpoints.sm}) {
    gap: ${tokens.spacing.md};
  }
`;

const InstructionItem = styled.li`
  counter-increment: instruction-counter;
  display: flex;
  align-items: flex-start;
  background: ${tokens.colors.surfaceMuted};
  padding: ${tokens.spacing.lg};
  border-radius: ${tokens.radius.md};
  box-shadow: ${tokens.shadow.sm};
  transition: all 0.2s ease;

  &:hover {
    transform: translateX(4px);
    box-shadow: ${tokens.shadow.md};
  }

  &::before {
    content: counter(instruction-counter);
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    height: 40px;
    background: ${tokens.colors.primary};
    color: ${tokens.colors.textOnDark};
    border-radius: 50%;
    font-weight: 700;
    font-size: ${tokens.typography.h3.size};
    margin-right: ${tokens.spacing.lg};
    flex-shrink: 0;
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing.md};

    &::before {
      min-width: 32px;
      height: 32px;
      font-size: ${tokens.typography.body.size};
      margin-right: ${tokens.spacing.md};
    }
  }
`;

const InstructionText = styled.span`
  color: ${tokens.colors.text};
  font-size: ${tokens.typography.body.size};
  line-height: ${tokens.typography.body.line};
  padding-top: 8px;

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: 14px;
    padding-top: 4px;
  }
`;

/**
 * Hook personalizado para buscar datos de descarga de la app
 */
const useAppDownload = () => {
  const apiCall = useCallback(
    async () => {
      return axios.get(`${API_BASE_URL}/social/app-download`);
    },
    []
  );

  return useApi<AppDownloadData>(apiCall, {
    immediate: true,
    dependencies: []
  });
};

/**
 * Componente principal de la página de descarga de la app
 */
const AppDownload: React.FC = () => {
  const { data, loading, error, execute } = useAppDownload();

  // Configurar meta tags y título de la página
  useEffect(() => {
    document.title = 'Cómo Descargar la App | Café Tutoriales';

    // Configurar meta descripción
    let metaTag = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute('name', 'description');
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', 'Aprenda cómo descargar e instalar la aplicación Café Tutoriales. Vea el video tutorial y siga las instrucciones paso a paso.');

    return () => {
      const metaTag = document.querySelector('meta[name="description"]');
      if (metaTag) {
        metaTag.remove();
      }
    };
  }, []);

  // Estado de carga
  if (loading) {
    return (
      <LoadingContainer role="status" aria-label="Cargando contenido">
        <LoadingSpinner />
        <LoadingText>Cargando información de descarga...</LoadingText>
      </LoadingContainer>
    );
  }

  // Estado de error
  if (error) {
    return (
      <ErrorContainer role="alert">
        <ErrorIcon>⚠️</ErrorIcon>
        <ErrorText>Error al cargar la información: {error}</ErrorText>
        <RetryButton onClick={execute}>
          Intentar de nuevo
        </RetryButton>
      </ErrorContainer>
    );
  }

  // Estado sin datos
  if (!data) {
    return (
      <ErrorContainer role="alert">
        <ErrorIcon>📱</ErrorIcon>
        <ErrorText>Información de descarga no disponible en este momento.</ErrorText>
      </ErrorContainer>
    );
  }

  // Renderizado principal
  return (
    <PageContainer>
      <PageTitle>Cómo Descargar la App</PageTitle>

      {/* Reproductor de video YouTube */}
      <VideoContainer>
        <VideoWrapper>
          <VideoIframe
            src={data.video_embed_url}
            title="Tutorial de Descarga de la App"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        </VideoWrapper>
      </VideoContainer>

      {/* Botón de descarga */}
      <DownloadButtonContainer>
        <DownloadButton
          href={data.download_url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${data.download_button_text} - Abre en nueva pestaña`}
        >
          {data.download_button_text}
        </DownloadButton>
      </DownloadButtonContainer>

      {/* Instrucciones paso a paso */}
      {data.instructions && data.instructions.length > 0 && (
        <InstructionsContainer>
          <InstructionsTitle>Instrucciones Paso a Paso</InstructionsTitle>
          <InstructionsList>
            {data.instructions.map((instruction, index) => (
              <InstructionItem key={index}>
                <InstructionText>{instruction}</InstructionText>
              </InstructionItem>
            ))}
          </InstructionsList>
        </InstructionsContainer>
      )}
    </PageContainer>
  );
};

export default AppDownload;
